import { useId, useState } from 'react';

/**
 * 377 (#1233, design `Hosts.html` A, G): a car's hosts — any number of the supplier's active Host memberships. The
 * chosen ones show as chips (removable); "Choose hosts" opens the supplier's hosts as a check-list. A chosen id that is
 * no longer an active Host of this supplier stays visible as "Former host" until removed, never silently dropped.
 */
export interface HostOption {
  id: string;
  name: string;
  /** 377: the host may see the supplier's sales (shown as a hint, design A). */
  can_see_sales?: boolean;
}

export function HostsPicker({
  value,
  onChange,
  options,
  label = 'Hosts',
  hint = 'The people at this supplier who look after this car. Each sees and handles all of its bookings. None: only admins handle it.',
  emptyText = 'No host',
}: {
  value: string[];
  onChange: (ids: string[]) => void;
  options: HostOption[];
  label?: string;
  hint?: string;
  emptyText?: string;
}) {
  const [open, setOpen] = useState(false);
  const listId = useId();
  const nameOf = (id: string) => options.find((o) => o.id === id)?.name ?? 'Former host';
  const toggle = (id: string) =>
    onChange(value.includes(id) ? value.filter((v) => v !== id) : [...value, id]);

  return (
    <div className="grid gap-1.5">
      <span className="text-sm font-medium text-ink">{label}</span>
      <div
        data-testid="hosts-chosen"
        className="flex min-h-10 flex-wrap items-center gap-1.5 rounded-md border border-hairline bg-surface px-2.5 py-1.5"
      >
        {value.length === 0 ? (
          <span className="text-sm text-steel">{emptyText}</span>
        ) : (
          value.map((id) => (
            <span
              key={id}
              className="inline-flex items-center gap-1 rounded-full bg-brand-blue-soft px-2.5 py-0.5 text-xs font-bold text-primary-deep"
            >
              {nameOf(id)}
              <button
                type="button"
                aria-label={`Remove ${nameOf(id)}`}
                className="opacity-60 hover:opacity-100"
                onClick={() => toggle(id)}
              >
                ✕
              </button>
            </span>
          ))
        )}
        <button
          type="button"
          className="ml-auto text-sm font-semibold text-primary"
          aria-expanded={open}
          aria-controls={listId}
          onClick={() => setOpen((o) => !o)}
        >
          {open ? 'Done' : 'Choose hosts ▾'}
        </button>
      </div>
      {open && (
        <div
          id={listId}
          className="grid max-w-sm gap-0.5 rounded-lg border border-hairline bg-surface p-1.5 shadow-sm"
        >
          {options.length === 0 ? (
            <p className="px-2 py-1.5 text-sm text-steel">This supplier has no active host.</p>
          ) : (
            options.map((o) => (
              <label
                key={o.id}
                className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm hover:bg-surface-feature"
              >
                <input
                  type="checkbox"
                  checked={value.includes(o.id)}
                  onChange={() => toggle(o.id)}
                />
                <span>{o.name}</span>
                {o.can_see_sales && <span className="text-xs text-steel">(sees sales)</span>}
              </label>
            ))
          )}
        </div>
      )}
      <span className="text-xs text-steel">{hint}</span>
    </div>
  );
}
